/**
 * Logger utility for consistent logging across the application
 * Provides structured logging with different levels and formatting
 *
 * This logger is safe for both client and server components.
 * For server-side OpenTelemetry integration, use lib/server-logger.ts instead.
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: string;
  data?: any;
}

class Logger {
  private context?: string;
  private logLevel: LogLevel;

  constructor(context?: string) {
    this.context = context;
    const isProduction = process.env.NODE_ENV === "production";
    const isBrowser = typeof window !== "undefined";
    const forceClientLogs = process.env.NEXT_PUBLIC_ENABLE_CLIENT_LOGS === "1";

    // Browser logs are user-visible. Keep production clients quiet unless explicitly enabled.
    if (isProduction && isBrowser && !forceClientLogs) {
      this.logLevel = LogLevel.ERROR;
      return;
    }

    // Keep server-side production logs available for diagnostics.
    this.logLevel = isProduction ? LogLevel.WARN : LogLevel.DEBUG;
  }

  private formatMessage(level: LogLevel, message: string, data?: any): string {
    const timestamp = new Date().toISOString();
    const levelStr = LogLevel[level];
    const contextStr = this.context ? `[${this.context}]` : "";
    let formattedMessage = `${timestamp} ${levelStr} ${contextStr} ${message}`;
    if (data !== undefined) {
      try {
        formattedMessage += ` ${JSON.stringify(data, null, 2)}`;
      } catch {
        formattedMessage += ` [Unable to stringify data]`;
      }
    }
    return formattedMessage;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  private log(level: LogLevel, message: string, data?: any): void {
    if (!this.shouldLog(level)) return;
    const formattedMessage = this.formatMessage(level, message, data);
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(formattedMessage);
        break;
      case LogLevel.INFO:
        console.info(formattedMessage);
        break;
      case LogLevel.WARN:
        console.warn(formattedMessage);
        break;
      case LogLevel.ERROR:
        console.error(formattedMessage);
        break;
    }
  }

  debug(message: string, data?: any): void {
    this.log(LogLevel.DEBUG, message, data);
  }
  info(message: string, data?: any): void {
    this.log(LogLevel.INFO, message, data);
  }
  warn(message: string, data?: any): void {
    this.log(LogLevel.WARN, message, data);
  }
  error(message: string, data?: any): void {
    this.log(LogLevel.ERROR, message, data);
  }
  child(childContext: string): Logger {
    const fullContext = this.context ? `${this.context}:${childContext}` : childContext;
    return new Logger(fullContext);
  }
}

// Default logger
export const logger = new Logger();

// Factory function
export function createLogger(context: string): Logger {
  return new Logger(context);
}

// Convenience functions
export const log = {
  debug: (message: string, data?: any) => logger.debug(message, data),
  info: (message: string, data?: any) => logger.info(message, data),
  warn: (message: string, data?: any) => logger.warn(message, data),
  error: (message: string, data?: any) => logger.error(message, data),
};
